/**
 * Textos de Aprobaciones que se arman fuera de un componente: los avisos
 * emergentes (`assignmentToasts`) de cuando te resuelven una solicitud o te
 * llega una nueva.
 *
 * ⚠️ ESTA REDACCIÓN ESTÁ ESPEJADA CON LA DE LA CAMPANA (FC-743).
 *
 * Desde que existe la pestaña «Novedades», **el mismo hecho se cuenta dos
 * veces**: el toast del momento y la tarjeta que persiste 7 días. Los textos de
 * la tarjeta los redacta BACKEND (`CrmBell/news/`, guía FC-747) y los de aquí
 * el front, así que si se escriben distinto el usuario lee dos versiones de lo
 * mismo en la misma pantalla.
 *
 * Por eso lo de abajo reproduce, palabra por palabra, lo que manda backend:
 *
 *   aprobada  → «Aprobaron tu solicitud del {N} % en {empresa}»
 *               «Por {quien} · ya puedes cerrar el negocio»
 *   rechazada → «Rechazaron tu solicitud en {empresa}»
 *               «Por {quien} · «{respuesta}»»
 *   pendiente → «{solicitante} pide {N} % de descuento en {empresa}»
 *               detalle = el comentario de la solicitud
 *
 * **Si backend cambia esos textos, hay que cambiarlos aquí también.** La
 * alternativa —que el toast se alimente de `news/`— no sirve: el toast sale del
 * socket, que llega antes y sin pasar por ese endpoint.
 *
 * Decidido con el usuario el 2026-10-26. Contexto en
 * `.claude/crm_notificaciones/` (§Contraste H3 y el contrato de backend).
 */
import { tieneDescuento } from "./approvalsApi";

/** Backend manda `resolved_by` casi siempre; si falta, se nombra el rol. */
const quienResolvio = (solicitud) => solicitud.resolvio || "el coordinador";

/** «12 %» con coma decimal, o `null` si el pedido no cambia el precio. */
const porcentaje = (solicitud) =>
  tieneDescuento(solicitud) ? `${String(solicitud.descuento).replace(".", ",")} %` : null;

/**
 * El aviso a quien resuelve cuando llega una solicitud nueva (FC-686 CA-05).
 *
 * «Ver solicitudes» NO se unificó con el «Ver interacción» de la campana a
 * propósito: este lleva a la **página de Aprobaciones**, que es donde se
 * resuelve, no al detalle de la interacción. Rótulos distintos porque los
 * destinos son distintos.
 */
export function avisoNuevaSolicitud(solicitud) {
  const pct = porcentaje(solicitud);
  const pide = pct ? `${pct} de descuento` : "una condición especial";
  const quien = solicitud.solicitante || "Alguien";
  return {
    tipo: "solicitud",
    titulo: `${quien} pide ${pide} en ${solicitud.empresa}`,
    texto: solicitud.comentario || "",
    accion: { label: "Ver solicitudes", routeName: "fx-approvals" },
  };
}

/**
 * El aviso al gestor cuando le resuelven la solicitud (task_3 CA-01).
 *
 * Abre el detalle en el panel lateral (`drawerId`), no la página completa, que
 * es justo lo que hace también el botón de la tarjeta: mismo destino, mismo
 * rótulo.
 */
export function avisoResolucion(solicitud) {
  const aprobada = solicitud.estado === "aprobada";
  const quien = quienResolvio(solicitud);
  const pct = porcentaje(solicitud);
  const titulo = aprobada
    ? `Aprobaron tu solicitud${pct ? ` del ${pct}` : ""} en ${solicitud.empresa}`
    : `Rechazaron tu solicitud en ${solicitud.empresa}`;
  const texto = aprobada
    ? `Por ${quien} · ya puedes cerrar el negocio`
    : `Por ${quien}${solicitud.respuesta ? ` · «${solicitud.respuesta}»` : ""}`;

  return {
    tipo: aprobada ? "aprobada" : "rechazada",
    titulo,
    texto,
    accion: solicitud.managementId
      ? { label: "Ver interacción", drawerId: solicitud.managementId }
      : null,
  };
}
